<template>
  <div class="phone-input">
    <div class="phone-input__icon">
      <slot name="icon">
        <img v-if="icon" :src="icon" alt="Icon">
      </slot>
    </div>

    <input
        id="input-field"
        :class="{ 'form-item--error': isValid == false, valid: isValid == true }"
        v-model="displayValue"
        type="tel" :placeholder="placeholder"
        @blur="formatPhoneNumber"
        @input="handleInput"
        inputmode="tel">

    <span v-if="isValid === false" class="phone-input__error-icon">
      ⚠
    </span>
  </div>
</template>

<script setup lang="ts">
  import { useMessagingService } from '@/composables/useMessagingService';
import { computed, ref, watch } from 'vue';

  const { normalizePhoneNumber, isValidPhoneNumber, formatDin5008 } = useMessagingService();

  interface Props {
    modelValue?: string;
    icon?: string;
    placeholder?: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: '',
    icon: '',
    placeholder: 'Telefonnummer',
  });

  const emit = defineEmits<{
    'update:modelValue': [value: string];
    valid: [value: string];
    invalid: [value: string];
  }>();

  const displayValue = computed({
    get: () => props.modelValue ?? '',
    set: (value: string) => {
      emit('update:modelValue', value);
    },
  });

  const isValid = ref(null);

  let timeout: number;

  watch(
    () => props.modelValue,
    (value) => {
      displayValue.value = value;
    },
  );

  function handleInput(): void {
    const value = normalizePhoneNumber(displayValue.value);

    if (isValidPhoneNumber(value)) {
      isValid.value = true;

      emit('valid', value);
    } else {
      isValid.value = false;
      emit('invalid', value);
    }

    if (timeout) {
      clearTimeout(timeout)
    }
  }

  function formatPhoneNumber(): void {
    const value = normalizePhoneNumber(displayValue.value);

    if (!isValidPhoneNumber(value)) {
      return;
    }

    const formattedValue = formatDin5008(value);

    emit('update:modelValue', formattedValue);
  }
</script>

<style scoped lang="scss">
  .phone-input {
    position: relative;
    display: flex;
    align-items: center;
  }

  .phone-input__icon {
    max-width: 24px;
    max-height: 24px;
    flex-shrink: 0;
  }

  .phone-input__icon img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  .phone-input__field {
    position: relative;
    flex: 1;
    display: flex;
  }

  input {
    // flex: 1;
    // padding: 0.5rem 2rem 0.5rem 0.75rem; // rechts Platz für Icon
    width: 100%;
    padding-right: 2rem; // Platz damit Text nicht unter dem Icon läuft
  }

  // input.invalid {
  //   border-color: $error-color !important;
  // }

  .phone-input__error-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #d40;
    font-size: 1.25rem;
    pointer-events: none;
  }
</style>
